// ── Status Constants (mirrors QCDoc.Core.Enums) ──

export const ProductStatus = {
  Pending: 10,
  Approved: 11,
  Inactive: 12,
} as const;
export type ProductStatus = (typeof ProductStatus)[keyof typeof ProductStatus];

/**
 * Mirrors legacy QCDOCBLL.VQCAStatus. Note the values track *completed* signatures,
 * not pending ones: 2 means the Global Quality Lead has signed (so it is awaiting the
 * Category Manager), and 3 means the Category Manager has signed.
 */
export const VqcaStatus = {
  Started: 1,
  PendingGQL: 2,
  PendingCM: 3,
  SignedByVendor: 4,
  Approved: 5,
  Rejected: 6,
  RejectedByCM: 7,
  Inactive: 8,
} as const;
export type VqcaStatus = (typeof VqcaStatus)[keyof typeof VqcaStatus];

/**
 * VQCA (New Vendor) Request status — mirrors legacy QCDOCBLL.VendorReqStatus
 * and the dbo.ReqStatus lookup table.
 */
export const VendorReqStatus = {
  Created: 1,
  ApprovedByCatMgr: 2,
  RejectedByCatMgr: 3,
  ApprovedByGblQltyMgr: 4,
  RejectedByGblQltyMgr: 5,
  VqcaGenerated: 6,
  SystemGenerated: 7,
  Saved: 8,
} as const;
export type VendorReqStatus = (typeof VendorReqStatus)[keyof typeof VendorReqStatus];

export const CoaStatus = {
  Created: 1,
  UnderReview: 2,
  Passed: 3,
  Failed: 4,
  ConditionallyApproved: 5,
} as const;
export type CoaStatus = (typeof CoaStatus)[keyof typeof CoaStatus];

export const SampleStatus = {
  Passed: 1,
  InProcess: 2,
  Failed: 3,
  ReOpen: 8,
  LoggedIn: 9,
} as const;
export type SampleStatus = (typeof SampleStatus)[keyof typeof SampleStatus];

// Mirrors QCDOC.Common.Enums.Common.STPStatus
export const StpStatus = {
  Pending: 13,
  Approved: 14,
  Inactive: 15,
} as const;
export type StpStatus = (typeof StpStatus)[keyof typeof StpStatus];

export const CriticalityLevel = {
  Level1: 1,
  Level2: 2,
  Level3: 3,
  Level4: 4,
} as const;
export type CriticalityLevel = (typeof CriticalityLevel)[keyof typeof CriticalityLevel];

// ── Application Roles (mirrors QCDoc.Core.Enums.AppRoles) ──

export const AppRoles = {
  // ── Core admin roles ──
  Admin: 'Admin',
  SuperAdmin: 'SuperAdmin',
  QCDocAdmin: 'QCDocAdmin',
  // ── Quality roles ──
  GQL: 'GQL',
  CM: 'CM',
  QALab: 'QALab',
  QALabSupervisor: 'QALabSupervisor',
  QATech: 'QATech',
  // ── View/read-only ──
  ReadOnly: 'ReadOnly',
  // ── Vendor portal ──
  Vendor: 'Vendor',
  VendorAdmin: 'VendorAdmin',
  // ── Module-specific ──
  ProductOwner: 'ProductOwner',
  PackagingAdmin: 'PackagingAdmin',
  COAAdmin: 'COAAdmin',
  ReportViewer: 'ReportViewer',
  // ── Legacy roles (maps from ASP.NET Membership) ──
  PurchasingAgent: 'PurchasingAgent',
  Manager: 'Manager',
  GroupLeader: 'GroupLeader',
  Logistics: 'Logistics',
  SupplyChain: 'SupplyChain',
  Regulatory: 'Regulatory',
  FormulaEditor: 'FormulaEditor',
  Hdfx: 'Hdfx',
} as const;

export type AppRole = (typeof AppRoles)[keyof typeof AppRoles];

// ── PSL Codes ──

export const PslCodes = {
  Baroid: 'BAR',
  Cementing: 'CEM',
  ProductionEnhancement: 'PE',
  MultiChem: 'MC',
} as const;

export type PslCode = (typeof PslCodes)[keyof typeof PslCodes];

// ── Authorization Policies (mirrors QCDoc.Api.Authorization.Policies) ──

export const Policies = {
  AdminOnly: 'AdminOnly',
  GqlOrAdmin: 'GqlOrAdmin',
  CmOrAdmin: 'CmOrAdmin',
  QALabAccess: 'QALabAccess',
  VendorAccess: 'VendorAccess',
  ReadAccess: 'ReadAccess',
  ProductManagement: 'ProductManagement',
  CoaManagement: 'CoaManagement',
  ReportAccess: 'ReportAccess',
  LogisticsAccess: 'LogisticsAccess',
  AlertManagement: 'AlertManagement',
  CategoryManagement: 'CategoryManagement',
  HdfxAccess: 'HdfxAccess',
  VqcaAccess: 'VqcaAccess',
  PackagingAccess: 'PackagingAccess',
} as const;

// ── Status label maps for UI display ──

export const ProductStatusLabel: Record<ProductStatus, string> = {
  [ProductStatus.Pending]: 'Pending',
  [ProductStatus.Approved]: 'Approved',
  [ProductStatus.Inactive]: 'Inactive',
};

export const VqcaStatusLabel: Record<VqcaStatus, string> = {
  [VqcaStatus.Started]: 'Started',
  [VqcaStatus.PendingGQL]: 'Signed By Global Quality Lead',
  [VqcaStatus.PendingCM]: 'Signed By Category Manager',
  [VqcaStatus.SignedByVendor]: 'Signed By Vendor',
  [VqcaStatus.Approved]: 'Approved',
  [VqcaStatus.Rejected]: 'Rejected',
  [VqcaStatus.RejectedByCM]: 'Rejected By Category Manager',
  [VqcaStatus.Inactive]: 'Inactive',
};

export const VendorReqStatusLabel: Record<VendorReqStatus, string> = {
  [VendorReqStatus.Created]: 'Created',
  [VendorReqStatus.ApprovedByCatMgr]: 'Approved By Category Manager',
  [VendorReqStatus.RejectedByCatMgr]: 'Rejected By Category Manager',
  [VendorReqStatus.ApprovedByGblQltyMgr]: 'Approved By Global Quality Lead',
  [VendorReqStatus.RejectedByGblQltyMgr]: 'Rejected By Global Quality Lead',
  [VendorReqStatus.VqcaGenerated]: 'VQCA Generated',
  [VendorReqStatus.SystemGenerated]: 'System Generated',
  [VendorReqStatus.Saved]: 'Saved',
};

export const CoaStatusLabel: Record<CoaStatus, string> = {
  [CoaStatus.Created]: 'Created',
  [CoaStatus.UnderReview]: 'Under Review',
  [CoaStatus.Passed]: 'Passed',
  [CoaStatus.Failed]: 'Failed',
  [CoaStatus.ConditionallyApproved]: 'Conditionally Approved',
};

export const SampleStatusLabel: Record<SampleStatus, string> = {
  [SampleStatus.Passed]: 'Passed',
  [SampleStatus.InProcess]: 'In Process',
  [SampleStatus.Failed]: 'Failed',
  [SampleStatus.ReOpen]: 'Re-Open',
  [SampleStatus.LoggedIn]: 'Logged In',
};

export const StpStatusLabel: Record<StpStatus, string> = {
  [StpStatus.Pending]: 'Pending',
  [StpStatus.Approved]: 'Approved',
  [StpStatus.Inactive]: 'Inactive',
};
